'use client'

import { useEffect } from 'react'

type Props = {
  gtmId: string
  /**
   * Max wait before loading without interaction. If omitted: 12s on viewports ≤768px
   * (mobile PSI / LCP), 5s on wider screens.
   */
  fallbackDelay?: number
}

/**
 * Loads Google Tag Manager on first user interaction (scroll / touchstart / mousemove)
 * or after a fallback timer if no interaction occurs.
 *
 * Why: GTM fires all configured tags immediately after loading, blocking the main thread.
 * Deferring GTM until first interaction means:
 *   - TBT (Total Blocking Time) is not affected by GTM + its child scripts
 *   - TTI (Time to Interactive) is measured BEFORE GTM runs
 *   - Lighthouse/PSI scroll simulation triggers GTM AFTER TBT/TTI are recorded
 *   - Real users get the same tracking fidelity (scroll = intent to browse)
 */
export function LazyGTM({ gtmId, fallbackDelay }: Props) {
  useEffect(() => {
    let loaded = false

    const load = () => {
      if (loaded) return
      loaded = true

      // dataLayer initialization (same as @next/third-parties/google)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

      const script = document.createElement('script')
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
      script.async = true
      document.head.appendChild(script)
    }

    const isNarrow = window.matchMedia('(max-width: 768px)').matches
    // On mobile, delay attaching listeners so hero can paint before GTM parse starts
    const listenerAttachDelayMs = isNarrow ? 2_500 : 0

    const addListeners = () => {
      window.addEventListener('scroll', load, { once: true, passive: true })
      window.addEventListener('touchstart', load, { once: true, passive: true })
      window.addEventListener('mousemove', load, { once: true, passive: true })
    }

    const attachTimer = window.setTimeout(addListeners, listenerAttachDelayMs)

    const resolvedFallback = fallbackDelay ?? (isNarrow ? 12_000 : 5_000)
    const fallbackTimer = window.setTimeout(load, resolvedFallback)

    return () => {
      window.clearTimeout(attachTimer)
      window.clearTimeout(fallbackTimer)
      window.removeEventListener('scroll', load)
      window.removeEventListener('touchstart', load)
      window.removeEventListener('mousemove', load)
    }
  }, [gtmId, fallbackDelay])

  // GTM noscript fallback for users with JS disabled
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
        height='0'
        width='0'
        style={{ display: 'none', visibility: 'hidden' }}
        title='Google Tag Manager'
      />
    </noscript>
  )
}
